import { CdkTrapFocus } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  Output,
  Pipe,
  PipeTransform,
  TemplateRef,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, getTrackByIdentityFn, InputObservable, trackByValue } from '@y42/angular/util';
import { AppUX, APP_UX } from '@y42/core/environment';
import { assertNonNullable } from '@y42/util/assert';
import { filteredArray, gapsRegExpFactory } from '@y42/util/filtered-array';
import { keys } from '@y42/util/object-utils';
import { combineLatest, debounceTime, map, Observable, shareReplay } from 'rxjs';
import { FilterableListItemContext, FilterableListItemDirective } from './filterable-list-item.directive';
import { FilterableListSummaryItemDirective } from './filterable-list-summary-item.directive';

@Pipe({ name: 'identify' })
export class IdentifyPipe<T> implements PipeTransform {
  transform(value: T, identityFn?: (value: T) => string): string {
    if (typeof value === 'string') {
      return value;
    }

    assertNonNullable(identityFn);
    return identityFn(value);
  }
}

export type FilterableItemsListMode = 'toggled-list' | 'list';

@UntilDestroy()
@Component({
  selector: 'y42-filterable-items-list',
  templateUrl: './filterable-items-list.component.html',
  styleUrls: ['./filterable-items-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterableItemsListComponent<T extends string | object, IdType extends string = string> {
  constructor(@Inject(APP_UX) private readonly appUX: AppUX) {}

  @ViewChild(CdkTrapFocus) trapFocus?: CdkTrapFocus;

  @Input()
  @InputObservable()
  listMode: FilterableItemsListMode = 'toggled-list';
  private readonly listMode$!: Observable<FilterableItemsListMode>;

  @Input()
  enableSelectedItemsListSummary = false;

  @Input()
  @InputObservable()
  items: T[] = [];
  private readonly items$!: Observable<T[]>;

  @Input()
  @InputObservable()
  selected: IdType[] = [];
  private readonly selected$!: Observable<IdType[]>;

  @Input()
  identityFn?: (item: T) => IdType;

  @Input()
  filterPropertyValueFn?: (item: T) => string;

  @Input()
  @InputObservable()
  searchPlaceholder = '';
  private readonly searchPlaceholder$!: Observable<string>;

  @Input()
  emptyListLabel = '';

  @Input()
  @InputObservable()
  enableCreateItemButton = false;
  private readonly enableCreateItemButton$!: Observable<boolean>;

  @Input()
  createItemButtonLabel = '';

  @Input()
  itemSize = 30;

  /**
   * Emits all selected values
   */
  @Output()
  selectionChange = new EventEmitter<IdType[]>();

  /**
   * Emits the most recently selected value
   */
  @Output()
  selectItem = new EventEmitter<string>();

  @Output()
  createItem = new EventEmitter<string>();

  readonly trackByValue: TrackByFunction<T> = trackByValue<T>;
  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly isUX3 = this.appUX === 'ux3';

  private readonly selectionMap$ = this.selected$.pipe(
    map((selected) => {
      return selected.reduce((selection, current) => {
        selection[this.getIdentity(current)] = true;
        return selection;
      }, {} as Record<string, true>);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly filteredArray = filteredArray({
    filter$: this.searchControl.valueChanges.pipe(debounceTime(100)),
    items$: this.items$,
    regExpFactory: gapsRegExpFactory,
    filterItemPropertyValueFn: (item) => this.getFilterItemPropertyValue(item),
  });

  readonly trackByIdentity = getTrackByIdentityFn<T>((item) => this.getIdentity(item));

  readonly state = connectState(this, {
    selection: this.selectionMap$,
    selectedItems: combineLatest([this.items$, this.selectionMap$]).pipe(
      map(([items, selection]) => {
        const identityFn = this.identityFn;
        if (identityFn) {
          return items.filter((item) => selection[identityFn(item)]);
        }

        return Object.keys(selection) as T[];
      }),
    ),
    items: combineLatest([this.filteredArray.items$, this.selectionMap$]).pipe(map(([items]) => items)),
    itemsFilter: this.filteredArray.filter$,
    searchPlaceholder: this.searchPlaceholder$,
    isEmptyListLabelEnabled: this.filteredArray.items$.pipe(map((items) => items.length === 0 && this.emptyListLabel)),
    isCreateButtonEnabled: combineLatest([
      this.enableCreateItemButton$,
      this.filteredArray.items$,
      this.searchControl.valueChanges,
    ]).pipe(
      debounceTime(100),
      map(
        ([enableCreateItemButton, items, value]) =>
          enableCreateItemButton && value && !items.find((item) => this.getFilterItemPropertyValue(item) === value),
      ),
    ),
    isToggledList: this.listMode$.pipe(map((listMode) => listMode === 'toggled-list')),
    isRegularList: this.listMode$.pipe(map((listMode) => listMode === 'list')),
  });

  @ContentChild(FilterableListItemDirective, { read: TemplateRef })
  readonly itemTemplate!: TemplateRef<FilterableListItemContext<T>>;
  @ContentChild(FilterableListSummaryItemDirective, { read: TemplateRef })
  readonly summaryItemTemplate!: TemplateRef<FilterableListSummaryItemDirective>;

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    event.stopPropagation();
  }

  create(name: string) {
    this.createItem.emit(name);
    this.searchControl.setValue('');
    this.trapFocus?.focusTrap.focusInitialElement();
  }

  setSelection(item: T, checked: boolean) {
    // This deliberately modifies the local state
    // for virtual-scroll to work as expected -
    // meaning that scrolling down and back up again keeps the checked state of a checkbox
    // in case the consuming component does not handle selection (like e.g. in Storybook).
    const selection = this.state.selection;
    const id = this.getIdentity(item);

    if (checked) {
      selection[id] = true;
      this.selectItem.emit(id);
    } else {
      delete selection[id];
    }

    this.selectionChange.emit(keys(selection));
  }

  private getFilterItemPropertyValue(item: T): string {
    const value = this.filterPropertyValueFn ? this.filterPropertyValueFn(item) : (item as string);
    return value;
  }

  private getIdentity(item: T | string): IdType {
    if (typeof item === 'string') {
      return item as IdType;
    }

    const identityFn = this.identityFn;
    assertNonNullable(identityFn, `[FilterableItemsList]: item is of type object, but "identityFn" is undefined`);
    return identityFn(item);
  }
}
